import { useEffect, useState } from 'react';
import { fetchData } from '../../../apiService';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, Snackbar, Alert,
  CircularProgress, Autocomplete, Checkbox, Chip,
  InputAdornment
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, Search, List, Print } from '@mui/icons-material';

const SaleModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  saleDetails,
  setSaleDetails,
  onSave,
  customers,
  items,
  banks,
  loading,
  companyId,
  branchId,
  token,
  onPrintInvoice
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isPaidAmountManuallySet, setIsPaidAmountManuallySet] = useState(false);

  // Get Items modal state
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemsPerPage] = useState(50);

  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Sale' : modalAction === 'edit' ? 'Edit Sale' : 'View Sale';

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setIsPaidAmountManuallySet(false); // Start with auto-adjustment enabled for both create and edit
    }
  }, [open, modalAction]);

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Validation
  const validateField = (field, value) => {
    switch (field) {
      case 'BillNumber':
        if (!value || value.toString().trim() === '') return 'Bill Number is required';
        return '';
      case 'BillDate':
        if (!value) return 'Bill Date is required';
        return '';
      case 'CustomerName':
        if (!value || value.trim() === '') return 'Customer is on list that can be selected or type the name';
        return '';
      case 'ContactNumber1':
        if (!value || value.trim() === '') return 'Contact Number 1 is required';
        return '';
      case 'PinCode':
        if (!value || value.trim() === '') return 'Pin Code is required';
        return '';
      case 'Location':
        if (!value || value.trim() === '') return 'Location is required';
        return '';
      case 'AddressLine1':
        if (!value || value.trim() === '') return 'Address Line 1 is required';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const billNumberError = validateField('BillNumber', formData.BillNumber);
    if (billNumberError) newErrors.BillNumber = billNumberError;

    const billDateError = validateField('BillDate', formData.BillDate);
    if (billDateError) newErrors.BillDate = billDateError;

    if (!formData.CustomerName || formData.CustomerName.trim() === '') {
      newErrors.CustomerName = 'Customer is on list that can be selected or type the name';
    }

    if (!formData.ContactNumber1 || formData.ContactNumber1.trim() === '') {
      newErrors.ContactNumber1 = 'Contact Number 1 is required';
    }

    if (!formData.PinCode || formData.PinCode.trim() === '') {
      newErrors.PinCode = 'Pin Code is required';
    }

    if (!formData.Location || formData.Location.trim() === '') {
      newErrors.Location = 'Location is required';
    }

    if (!formData.AddressLine1 || formData.AddressLine1.trim() === '') {
      newErrors.AddressLine1 = 'Address Line 1 is required';
    }

    if (saleDetails.length === 0) {
      newErrors.saleDetails = 'At least one item is required';
    }

    // Validate negative balance
    const balanceAmount = parseFloat(formData.BalanceAmount) || 0;
    if (balanceAmount < 0) {
      newErrors.BalanceAmount = 'Balance amount cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleSaveWithValidation = () => {
    setTouched({ 
      BillNumber: true, 
      BillDate: true, 
      CustomerName: true,
      ContactNumber1: true,
      PinCode: true,
      Location: true,
      AddressLine1: true
    });

    const validationMessages = [];

    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') {
      validationMessages.push('Bill Number is required');
    }
    if (!formData.BillDate) {
      validationMessages.push('Bill Date is required');
    }
    if (!formData.CustomerName || formData.CustomerName.trim() === '') {
      validationMessages.push('Customer is on list that can be selected or type the name');
    }
    if (!formData.ContactNumber1 || formData.ContactNumber1.trim() === '') {
      validationMessages.push('Contact Number 1 is required');
    }
    if (!formData.PinCode || formData.PinCode.trim() === '') {
      validationMessages.push('Pin Code is required');
    }
    if (!formData.Location || formData.Location.trim() === '') {
      validationMessages.push('Location is required');
    }
    if (!formData.AddressLine1 || formData.AddressLine1.trim() === '') {
      validationMessages.push('Address Line 1 is required');
    }
    if (saleDetails.length === 0) {
      validationMessages.push('At least one item is required');
    }

    // Validate negative balance
    const balanceAmount = parseFloat(formData.BalanceAmount) || 0;
    if (balanceAmount < 0) {
      validationMessages.push('Balance amount cannot be negative');
    }

    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }

    if (validateForm()) {
      onSave();
    }
  };

  const handleChange = (field, value) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    if (field === 'CustomerName') {
      const selectedCustomer = customers.find(c => c.Name === value);
      if (selectedCustomer) {
        setFormData(prev => ({
          ...prev,
          CustomerId: selectedCustomer.Id || selectedCustomer.MasterId,
          CustomerName: value,
          ContactNumber1: selectedCustomer.ContactNumber1 || '',
          ContactNumber2: selectedCustomer.ContactNumber2 || '',
          AddressLine1: selectedCustomer.AddressLine1 || '',
          AddressLine2: selectedCustomer.AddressLine2 || '',
          Location: selectedCustomer.Location || '',
          PinCode: selectedCustomer.PinCode || ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          CustomerId: null,
          CustomerName: value,
          ContactNumber1: '',
          ContactNumber2: '',
          AddressLine1: '',
          AddressLine2: '',
          Location: '',
          PinCode: ''
        }));
      }

      if (errors.CustomerName && value && value.trim() !== '') {
        setErrors(prev => ({ ...prev, CustomerName: '' }));
      }
    } else if (field === 'IsGST') {
      // Update GST setting and recalculate all items
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Reset paid amount flag when GST changes so it auto-adjusts to new total
      setIsPaidAmountManuallySet(false);
      
      // Recalculate all sale details with new GST setting
      const updatedDetails = saleDetails.map(detail => {
        const quantity = Math.max(0, parseFloat(detail.Quantity) || 0);
        const rate = Math.max(0, parseFloat(detail.Rate) || 0);
        const gstRate = value ? Math.max(0, parseFloat(detail.GSTRate) || 0) : 0;
        const discountPct = parseFloat(detail.DiscountPercentage) || 0;

        const amount = quantity * rate;
        const discountAmount = (amount * discountPct) / 100;
        const taxableAmount = amount - discountAmount;
        const gstAmount = value ? (taxableAmount * gstRate) / 100 : 0;

        return {
          ...detail,
          CGSTAmount: value ? Math.round((gstAmount / 2) * 100) / 100 : 0,
          SGSTAmount: value ? Math.round((gstAmount / 2) * 100) / 100 : 0,
          IGSTAmount: 0,
          TotalAmount: Math.round((taxableAmount + (value ? gstAmount : 0)) * 100) / 100
        };
      });
      
      setSaleDetails(updatedDetails);
      // Delay calculation to ensure state is updated
      setTimeout(() => {
        calculateTotals(updatedDetails);
      }, 10);
    } else if (field === 'PaidAmount') {
      const newValue = parseFloat(value) || 0;
      const totalAmount = parseFloat(formData.TotalAmount) || 0;

      if (newValue > totalAmount) {
        showError(`Paid amount (₹${newValue.toFixed(2)}) cannot exceed total amount (₹${totalAmount.toFixed(2)})`);
        return;
      }

      const balanceAmount = totalAmount - newValue;

      setIsPaidAmountManuallySet(true); // Mark as manually set

      setFormData(prev => ({
        ...prev,
        PaidAmount: newValue,
        BalanceAmount: balanceAmount
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    const updatedDetails = [...saleDetails];

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setSaleDetails(updatedDetails);
        calculateTotals(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const quantity = Math.max(0, parseFloat(detail.Quantity) || 0);
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0) ; // Set GST to 0 if IsGST is false

      const amount = quantity * rate;
      let discountAmount = 0, discountPct = 0;

      if (field === 'DiscountAmount') {
        discountAmount = Math.max(0, parseFloat(value) || 0);
        discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
      } else {
        discountPct = parseFloat(detail.DiscountPercentage) || 0;
        discountAmount = (amount * discountPct) / 100;
      }

      const taxableAmount = amount - discountAmount;
      const gstAmount = formData.IsGST ? (taxableAmount * gstRate) / 100 : 0; // Set GST amount to 0 if IsGST is false

      updatedDetails[index] = {
        ...updatedDetails[index],
        Amount: Math.round(amount * 100) / 100,
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        CGSTAmount: formData.IsGST ? Math.round((gstAmount / 2) * 100) / 100 : 0,
        SGSTAmount: formData.IsGST ? Math.round((gstAmount / 2) * 100) / 100 : 0,
        IGSTAmount: 0,
        TotalAmount: Math.round((taxableAmount + (formData.IsGST ? gstAmount : 0)) * 100) / 100
      };
    }
    setSaleDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleItemSelect = (index, itemId) => {
    const isDuplicate = saleDetails.some((detail, i) =>
      i !== index && detail.ItemId === parseInt(itemId)
    );

    if (isDuplicate) {
      showError('This item is already added to the table. Please select a different item.');
      return;
    }

    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = [...saleDetails];
      const quantity = parseFloat(updatedDetails[index].Quantity) || 0;
      const rate = selectedItem.SaleRate || 0;
      const gstRate = selectedItem.GSTRate || 0 ; // Set GST to 0 if IsGST is false
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;

      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const taxableAmount = amount - discountAmount;
      const gstAmount = formData.IsGST ? (taxableAmount * gstRate) / 100 : 0; // Set GST amount to 0 if IsGST is false

      updatedDetails[index] = {
        ...updatedDetails[index],
        ItemId: itemId,
        HSNCode: selectedItem.HSNCode || '',
        GSTRate: gstRate,
        Rate: Math.round(rate * 100) / 100,
        Amount: Math.round(amount * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        CGSTAmount: formData.IsGST ? Math.round((gstAmount / 2) * 100) / 100 : 0,
        SGSTAmount: formData.IsGST ? Math.round((gstAmount / 2) * 100) / 100 : 0,
        IGSTAmount: 0,
        TotalAmount: Math.round((taxableAmount + (formData.IsGST ? gstAmount : 0)) * 100) / 100
      };
      setSaleDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  };

  const addDetailRow = () => {
    setHasUnsavedChanges(true);
    setSaleDetails([{
      Id: 0, ItemId: '', ItemName: '',
      Quantity: 0, Rate: 0, Amount: 0, DiscountPercentage: 0, DiscountAmount: 0,
      HSNCode: '', GSTRate: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, IsActive: true
    }, ...saleDetails]);
  };

  const removeDetailRow = (index) => {
    setHasUnsavedChanges(true);
    const updatedDetails = saleDetails.filter((_, i) => i !== index);
    setSaleDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const calculateTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      NetAmount: acc.NetAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, NetAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    const transportingCharge = parseFloat(formData.TransportingCharge) || 0;
    const roundOff = Math.round(totals.TotalAmount + transportingCharge) - (totals.TotalAmount + transportingCharge);
    const finalTotalAmount = Math.round(totals.TotalAmount + transportingCharge);

    const currentPaid = parseFloat(formData.PaidAmount) || 0;

    let paidAmount = currentPaid;

    // Auto-set paid amount when user hasn't manually changed it (both create and edit modes)
    if (finalTotalAmount === 0) {
      paidAmount = 0;
    } else if (!isPaidAmountManuallySet) {
      paidAmount = finalTotalAmount;
    }

    const balanceAmount = finalTotalAmount - paidAmount;

    setFormData(prev => ({
      ...prev,
      Amount: Math.round(totals.Amount * 100) / 100,
      DiscountAmount: Math.round(totals.DiscountAmount * 100) / 100,
      NetAmount: Math.round(totals.NetAmount * 100) / 100,
      CGSTAmount: Math.round(totals.CGSTAmount * 100) / 100,
      SGSTAmount: Math.round(totals.SGSTAmount * 100) / 100,
      IGSTAmount: Math.round(totals.IGSTAmount * 100) / 100,
      TransportingCharge: Math.round(transportingCharge * 100) / 100,
      RoundOff: Math.round(roundOff * 100) / 100,
      TotalAmount: Math.round(finalTotalAmount * 100) / 100,
      PaidAmount: Math.round(paidAmount * 100) / 100,
      BalanceAmount: Math.round(balanceAmount * 100) / 100
    }));
  };

  useEffect(() => {
    if (!isViewMode && modalAction === 'create') {
      calculateTotals(saleDetails);
    }
  }, [formData.TransportingCharge]);

  useEffect(() => {
    if (!isViewMode && modalAction === 'edit' && saleDetails.length > 0) {
      calculateTotals(saleDetails);
    }
  }, [formData.TransportingCharge]);

  // Handle Get Items modal
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);

    const loadingTimeout = setTimeout(() => {
      console.warn('Get Items loading timeout - stopping loading');
      setGetItemsLoading(false);
    }, 3000);

    try {
      // Items are already loaded in the parent component
      console.log('Items already available:', items.length);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      clearTimeout(loadingTimeout);
      setGetItemsLoading(false);
    }
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.SaleRate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Update selected item details
  const handleSelectedItemChange = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item =>
      item.Id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Filter items based on search value for Get Items modal
  const getFilteredItems = () => {
    if (!getItemsSearchValue.trim()) {
      return items;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return items.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm) ||
      item.Code?.toLowerCase().includes(searchTerm)
    );
  };

  // Get paginated items for Get Items modal
  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = Math.ceil(getFilteredItems().length / itemsPerPage);

  // Check if item is already in sale details
  const isItemAlreadyInSale = (itemId) => {
    return saleDetails.some(detail => detail.ItemId === parseInt(itemId));
  };

  // Add selected items to sale details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const itemsToAdd = selectedItems.filter(
        selectedItem => !isItemAlreadyInSale(selectedItem.Id)
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const newItems = itemsToAdd.map(selectedItem => {
        const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
        const rate = parseFloat(selectedItem.selectedRate) || 0;
        const gstRate = parseFloat(selectedItem.GSTRate) || 0 ; // Set GST to 0 if IsGST is false
        const discountPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
        const discountAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
        const amount = quantity * rate;

        const finalDiscountAmount = discountAmt > 0 ? discountAmt : (amount * discountPct) / 100;
        const taxableAmount = amount - finalDiscountAmount;
        const gstAmount = formData.IsGST ? (taxableAmount * gstRate) / 100 : 0; // Set GST amount to 0 if IsGST is false

        return {
          Id: 0,
          ItemId: selectedItem.Id,
          HSNCode: selectedItem.HSNCode || '',
          GSTRate: gstRate,
          Quantity: Math.round(quantity * 100) / 100,
          Rate: Math.round(rate * 100) / 100,
          Amount: Math.round(amount * 100) / 100,
          DiscountPercentage: discountAmt > 0 ? Math.round(((finalDiscountAmount / amount) * 100) * 100) / 100 : Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(finalDiscountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: formData.IsGST ? Math.round((gstAmount / 2) * 100) / 100 : 0,
          SGSTAmount: formData.IsGST ? Math.round((gstAmount / 2) * 100) / 100 : 0,
          IGSTAmount: 0,
          TotalAmount: Math.round((taxableAmount + (formData.IsGST ? gstAmount : 0)) * 100) / 100,
          IsActive: true
        };
      });

      const updatedDetails = [...saleDetails, ...newItems];
      setSaleDetails(updatedDetails);

      handleCloseGetItemsModal();

      // Calculate totals
      setTimeout(() => {
        calculateTotals(updatedDetails);
      }, 10);

    } catch (error) {
      console.error('Error adding selected items:', error);
      showError('Failed to add selected items. Please try again.');
    } finally {
      setAddingItemsLoading(false);
    }
  };

  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getCustomerDisplayName = () => {
    return formData.CustomerName || '';
  };

  const CustomerDisplayName = getCustomerDisplayName();

  const disabledFieldStyle = { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } };
  const compactInput = { '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' }, ...(isViewMode && disabledFieldStyle) };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxWidth: '1400px', position: 'relative' } }}
      >
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: isViewMode ? '1px solid #e2e8f0' : '1px solid #d1fae5', bgcolor: isViewMode ? '#1e293b' : '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: isViewMode ? '#f8fafc' : '#059669', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isViewMode ? '#f8fafc' : '#064e3b', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ p: 0.5, color: isViewMode ? '#f8fafc' : 'inherit' }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: isViewMode ? '#f8fafc' : '#fefffe' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669' }}>Sale Information</Typography>
          </Box>

          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={12} sm={4}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Customer" value={CustomerDisplayName || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
              ) : (
                <Autocomplete
                  freeSolo
                  options={[
                    'Create New Customer',
                    ...customers.map(customer => customer.Name)
                  ]}
                  value={formData.CustomerName || CustomerDisplayName || ''}
                  onChange={(event, newValue) => {
                    if (newValue === 'Create New Customer') {
                      setFormData(prev => ({
                        ...prev,
                        CustomerId: 0,
                        CustomerName: '',
                        ContactNumber1: '',
                        ContactNumber2: '',
                        AddressLine1: '',
                        AddressLine2: '',
                        Location: '',
                        PinCode: ''
                      }));
                    } else {
                      handleChange('CustomerName', newValue || '');
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (newInputValue !== 'Create New Customer') {
                      setCustomerInputValue(newInputValue);
                      handleChange('CustomerName', newInputValue);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} style={{
                      fontSize: '0.85rem',
                      fontWeight: option === 'Create New Customer' ? 600 : 400,
                      color: option === 'Create New Customer' ? '#059669' : 'inherit',
                      borderBottom: option === 'Create New Customer' ? '1px solid #d1fae5' : 'none',
                      backgroundColor: option === 'Create New Customer' ? '#f0fdf4' : 'inherit'
                    }}>
                      {option === 'Create New Customer' ? '+ Create New Customer' : option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Customer *"
                      error={touched.CustomerName && !!errors.CustomerName}
                      helperText={touched.CustomerName && errors.CustomerName}
                      onBlur={() => handleBlur('CustomerName')}
                      sx={{
                        ...compactInput,
                        '& .MuiInputBase-root': {
                          height: '32px', // Reduced height to match other compact fields
                        },
                        '& .MuiInputBase-input': {
                          py: 0.5,
                          fontSize: '0.75rem'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                  )}
                  sx={{ 
                    width: '100%',
                    '& .MuiAutocomplete-inputRoot': {
                      paddingRight: '39px !important', // Adjust for dropdown arrow
                    }
                  }}
                  ListboxProps={{
                    style: { maxHeight: 200, fontSize: '0.85rem' }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Bill No *"
                value={formData.BillNumber || ''}
                onChange={(e) => handleChange('BillNumber', e.target.value)}
                onBlur={() => handleBlur('BillNumber')}
                disabled={isViewMode}
                error={touched.BillNumber && !!errors.BillNumber}
                helperText={touched.BillNumber && errors.BillNumber}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': {
                    py: 0.5,
                    fontSize: '0.75rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Bill Date *"
                value={formData.BillDate || ''}
                onChange={(e) => handleChange('BillDate', e.target.value)}
                onBlur={() => handleBlur('BillDate')}
                disabled={isViewMode}
                error={touched.BillDate && !!errors.BillDate}
                helperText={touched.BillDate && errors.BillDate}
                InputLabelProps={{ shrink: true }}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': {
                    py: 0.5,
                    fontSize: '0.75rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Fin Year"
                value={formData.FinYear || ''}
                onChange={(e) => handleChange('FinYear', e.target.value)}
                disabled={isViewMode}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': {
                    py: 0.5,
                    fontSize: '0.75rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small" sx={{
                ...compactInput,
                '& .MuiInputBase-root': {
                  height: '32px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem'
                }
              }}>
                <InputLabel>GST</InputLabel>
                <Select
                  value={formData.IsGST ? 1 : 0}
                  onChange={(e) => handleChange('IsGST', e.target.value === 1)}
                  disabled={isViewMode}
                  label="GST"
                  sx={{
                    fontSize: '0.75rem',
                    height: '32px',
                    '& .MuiSelect-select': {
                      backgroundColor: formData.IsGST ? '#dcfce7' : '#fef2f2',
                      fontWeight: 600,
                      color: formData.IsGST ? '#059669' : '#dc2626',
                      py: 0.5
                    }
                  }}
                >
                  <MenuItem value={1} sx={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>Yes</MenuItem>
                  <MenuItem value={0} sx={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Customer Details Section */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669', mb: 1, display: 'block' }}>Customer Details</Typography>
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={1.5} sm={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Contact No 1 *"
                value={formData.ContactNumber1 || ''}
                onChange={(e) => handleChange('ContactNumber1', e.target.value)}
                onBlur={() => handleBlur('ContactNumber1')}
                disabled={isViewMode}
                error={touched.ContactNumber1 && !!errors.ContactNumber1}
                helperText={touched.ContactNumber1 && errors.ContactNumber1}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                }}
              />
            </Grid>
            <Grid item xs={1.5} sm={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Contact No 2"
                value={formData.ContactNumber2 || ''}
                onChange={(e) => handleChange('ContactNumber2', e.target.value)}
                disabled={isViewMode}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                }}
              />
            </Grid>
            <Grid item xs={1.5} sm={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Location *"
                value={formData.Location || ''}
                onChange={(e) => handleChange('Location', e.target.value)}
                onBlur={() => handleBlur('Location')}
                disabled={isViewMode}
                error={touched.Location && !!errors.Location}
                helperText={touched.Location && errors.Location}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                }}
              />
            </Grid>
            <Grid item xs={1.5} sm={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Pin Code *"
                value={formData.PinCode || ''}
                onChange={(e) => handleChange('PinCode', e.target.value)}
                onBlur={() => handleBlur('PinCode')}
                disabled={isViewMode}
                error={touched.PinCode && !!errors.PinCode}
                helperText={touched.PinCode && errors.PinCode}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                }}
              />
            </Grid>
            <Grid item xs={3} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Address Line 1 *"
                value={formData.AddressLine1 || ''}
                onChange={(e) => handleChange('AddressLine1', e.target.value)}
                onBlur={() => handleBlur('AddressLine1')}
                disabled={isViewMode}
                error={touched.AddressLine1 && !!errors.AddressLine1}
                helperText={touched.AddressLine1 && errors.AddressLine1}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                }}
              />
            </Grid>
            <Grid item xs={3} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Address Line 2"
                value={formData.AddressLine2 || ''}
                onChange={(e) => handleChange('AddressLine2', e.target.value)}
                disabled={isViewMode}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-root': {
                    height: '32px',
                  },
                  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Items Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669' }}>Items</Typography>
            {!isViewMode && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<List />}
                onClick={handleOpenGetItemsModal}
                sx={{
                  borderColor: '#1e40af',
                  color: '#1e40af',
                  '&:hover': {
                    borderColor: '#1d4ed8',
                    backgroundColor: '#eff6ff'
                  }
                }}
              >
                Get Items
              </Button>
            )}
          </Box>

          {/* Desktop Table View */}
          {!isMobile && (
            <Box sx={{ border: '1px solid #d1fae5', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
                  <tr>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.75rem' }}>#</th>
                    <th style={{ padding: '8px 8px', textAlign: 'left', color: 'white', fontWeight: 600, fontSize: '0.75rem', minWidth: '200px' }}>Item Name</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.75rem', width: '80px' }}>HSN</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.75rem', width: '70px' }}>Qty</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.75rem', width: '85px' }}>Rate</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.75rem', width: '60px' }}>Disc%</th>
                    <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.75rem', width: '60px' }}>GST%</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.75rem', width: '95px' }}>Taxable</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.75rem', width: '95px' }}>Total</th>
                    {!isViewMode && <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.75rem', width: '50px' }}>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {saleDetails.map((d, index) => (
                    <tr key={index} style={{ background: index % 2 === 0 ? '#f0fdf4' : '#ffffff', borderBottom: '1px solid #d1fae5' }}>
                      <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 500, color: '#059669' }}>{index + 1}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 500, color: '#064e3b' }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#064e3b' }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || d.ItemName || '-'}</span>
                        ) : (
                          <FormControl fullWidth size="small">
                            <Select
                              value={d.ItemId || ''}
                              onChange={(e) => handleItemSelect(index, e.target.value)}
                              sx={{ 
                                fontSize: '0.75rem',
                                height: '28px',
                                '& .MuiSelect-select': {
                                  padding: '2px 8px',
                                  fontSize: '0.75rem'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #d1d5db'
                                }
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: 200,
                                    '& .MuiMenuItem-root': {
                                      fontSize: '0.75rem',
                                      padding: '4px 8px',
                                      minHeight: 'auto'
                                    }
                                  }
                                }
                              }}
                            >
                              <MenuItem value="" disabled>Select Item</MenuItem>
                              {items.map(item => (
                                <MenuItem key={item.Id} value={item.Id} sx={{ fontSize: '0.75rem' }}>
                                  {item.Name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'center', color: '#6b7280', fontSize: '0.7rem' }}>{d.HSNCode || '-'}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600, color: '#064e3b' }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#064e3b' }}>{d.Quantity || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.Quantity ?? ''} onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#064e3b' }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#064e3b' }}>{d.Rate || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.Rate ?? ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600, color: '#064e3b' }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#064e3b' }}>{d.DiscountPercentage || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.DiscountPercentage ?? 0} onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, max: 100, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600, color: '#059669' }}>{d.GSTRate || 0}%</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#064e3b' }}>{formatCurrency(d.TaxableAmount)}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(d.TotalAmount)}</td>
                      {!isViewMode && (
                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                          <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                        </td>
                      )}
                    </tr>
                  ))}
                  {saleDetails.length === 0 && (
                    <tr><td colSpan={isViewMode ? 9 : 10} style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: isViewMode ? '#f1f5f9' : '#f0fdf4' }}>No items added</td></tr>
                  )}
                </tbody>
              </table>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Totals Section */}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={8}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669', mb: 1, display: 'block' }}>Additional Charges & Payment</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f0fdf4', p: 1.5, borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField 
                      fullWidth 
                      size="small" 
                      label="Transporting Charge" 
                      type="number" 
                      value={formData.TransportingCharge || ''} 
                      onChange={(e) => handleChange('TransportingCharge', e.target.value)} 
                      disabled={isViewMode} 
                      inputProps={{ min: 0, style: { textAlign: 'right' } }} 
                      sx={{
                        ...compactInput,
                        '& .MuiInputBase-root': {
                          height: '40px'
                        },
                        '& .MuiInputBase-input': {
                          py: 0.75,
                          fontSize: '0.8rem'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.8rem'
                        }
                      }} 
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField 
                      fullWidth 
                      size="small" 
                      label="Paid Amount" 
                      type="number" 
                      value={formData.PaidAmount || ''} 
                      onChange={(e) => handleChange('PaidAmount', e.target.value)} 
                      disabled={isViewMode} 
                      inputProps={{ min: 0, style: { textAlign: 'right', fontWeight: 'bold', color: '#10b981' } }} 
                      sx={{
                        ...compactInput,
                        '& .MuiInputBase-root': {
                          height: '40px'
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 'bold',
                          color: '#10b981',
                          fontSize: '0.8rem'
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#10b981'
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#10b981',
                            borderWidth: '2px'
                          },
                          '&:hover fieldset': {
                            borderColor: '#059669'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#10b981'
                          }
                        },
                        '& .MuiInputBase-input': {
                          fontWeight: 'bold',
                          color: '#10b981',
                          fontSize: '0.85rem',
                          py: 0.75,
                          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          },
                          '&[type=number]': {
                            MozAppearance: 'textfield'
                          }
                        }
                      }} 
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <TextField 
                      fullWidth 
                      size="small" 
                      label="Balance Amount" 
                      type="number" 
                      value={formData.BalanceAmount || ''} 
                      onChange={(e) => handleChange('PaidAmount', e.target.value)} 
                      disabled 
                      inputProps={{ min: 0, style: { textAlign: 'right', fontWeight: 'bold', color: '#dc2626' } }} 
                      sx={{
                        ...compactInput,
                        '& .MuiInputBase-root': {
                          height: '40px'
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 'bold',
                          color: '#dc2626',
                          fontSize: '0.8rem'
                        },
                        '& .MuiInputLabel-root.Mui-disabled': {
                          color: '#dc2626'
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#dc2626',
                            borderWidth: '2px'
                          }
                        },
                        '& .MuiInputBase-input.Mui-disabled': {
                          fontWeight: 'bold',
                          color: '#dc2626',
                          WebkitTextFillColor: '#dc2626',
                          fontSize: '0.85rem',
                          py: 0.75
                        }
                      }} 
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <FormControl fullWidth size="small" sx={{
                      ...compactInput,
                      '& .MuiInputBase-root': {
                        height: '40px'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.8rem'
                      },
                      '& .MuiSelect-select': {
                        py: 0.75
                      }
                    }}>
                      <InputLabel>Mode of Payment</InputLabel>
                      <Select
                        value={formData.ModeOfPayment || 1}
                        onChange={(e) => handleChange('ModeOfPayment', e.target.value)}
                        disabled={isViewMode}
                        label="Mode of Payment"
                        sx={{ fontSize: '0.8rem' }}
                      >
                        <MenuItem value={1}>Cash</MenuItem>
                        <MenuItem value={2}>Card</MenuItem>
                        <MenuItem value={3}>UPI</MenuItem>
                        <MenuItem value={4}>Bank Transfer</MenuItem>
                        <MenuItem value={5}>Cheque</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField 
                      fullWidth 
                      size="small" 
                      label="Remarks" 
                      value={formData.Remarks || ''} 
                      onChange={(e) => handleChange('Remarks', e.target.value)} 
                      disabled={isViewMode} 
                      sx={{
                        ...compactInput,
                        '& .MuiInputBase-root': {
                          height: '40px'
                        },
                        '& .MuiInputBase-input': {
                          py: 0.75,
                          fontSize: '0.8rem'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.8rem'
                        }
                      }} 
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#059669', mb: 1, display: 'block' }}>Summary</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f0fdf4', p: 1.5, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Subtotal</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.Amount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Discount</Typography><Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>-{formatCurrency(formData.DiscountAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Net Amount</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.NetAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>CGST</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.CGSTAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>SGST</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.SGSTAmount)}</Typography></Box>
                {(parseFloat(formData.IGSTAmount) || 0) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>IGST</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.IGSTAmount)}</Typography></Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Transport</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCurrency(formData.TransportingCharge)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Round Off</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{formData.RoundOff >= 0 ? '+' : ''}{(formData.RoundOff || 0).toFixed(2)}</Typography></Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#059669' }}>{formatCurrency(formData.TotalAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Paid Amount</Typography><Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981' }}>{formatCurrency(formData.PaidAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Balance</Typography><Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626' }}>{formatCurrency(formData.BalanceAmount)}</Typography></Box>
              </Box>
            </Grid>
          </Grid>

        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: isViewMode ? '1px solid #e2e8f0' : '1px solid #d1fae5', bgcolor: isViewMode ? '#f8fafc' : '#f0fdf4' }}>
          <Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
          {isViewMode && onPrintInvoice && (
            <Button 
              onClick={() => onPrintInvoice(formData.Id)} 
              variant="outlined" 
              size="small"
              startIcon={<Print />}
              sx={{ color: '#7c3aed', borderColor: '#7c3aed' }}
            >
              Print Invoice
            </Button>
          )}
          {!isViewMode && <Button onClick={handleSaveWithValidation} variant="contained" size="small" disabled={loading}>Save</Button>}
        </DialogActions>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '6px',
            m: isMobile ? 0 : 0.5,
            maxHeight: '75vh',
            width: isMobile ? '100%' : '85%',
            maxWidth: '1000px'
          }
        }}
      >
        <DialogTitle sx={{
          py: 0.5,
          px: 1.5,
          borderBottom: '1px solid #d1d5db',
          bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: '#fff'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <List sx={{ color: '#fff', fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                Get Items ({getFilteredItems().length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ color: '#fff', p: 0.25 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ ml: 1.5, color: '#6b7280', fontSize: '0.8rem' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 14, color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                    endAdornment: getItemsSearchValue && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleGetItemsSearch('')}
                          sx={{ p: 0.25 }}
                        >
                          <Close sx={{ fontSize: 12 }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' },
                    '& .MuiOutlinedInput-root': { bgcolor: '#fff' }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 8).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    ))}
                    {selectedItems.length > 8 && (
                      <Chip label={`+${selectedItems.length - 8} more`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: '20px' }} />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table */}
              <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', height: '28px' }}>
                        <th style={{ padding: '3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems.length === getPaginatedItems().length && getPaginatedItems().length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems().length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                getPaginatedItems().forEach(item => {
                                  if (!selectedItems.find(si => si.Id === item.Id)) {
                                    handleItemSelection(item, true);
                                  }
                                });
                              } else {
                                getPaginatedItems().forEach(item => {
                                  handleItemSelection(item, false);
                                });
                              }
                            }}
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                          />
                        </th>
                        <th style={{ padding: '3px 6px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '30%', fontSize: '0.65rem' }}>Item</th>
                        <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>HSN</th>
                        <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem' }}>GST%</th>
                        <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Stock</th>
                        <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem' }}>Qty</th>
                        <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Rate</th>
                        <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem' }}>Disc%</th>
                        <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems().map((item, index) => {
                        const selectedItem = selectedItems.find(si => si.Id === item.Id);
                        const isSelected = !!selectedItem;
                        const isAlreadyAdded = isItemAlreadyInSale(item.Id);

                        return (
                          <tr key={item.Id} style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: isSelected ? '#eff6ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                            opacity: isAlreadyAdded ? 0.5 : 1,
                            height: '30px'
                          }}>
                            <td style={{ padding: '3px', textAlign: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isAlreadyAdded}
                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                              />
                            </td>
                            <td style={{ padding: '3px 6px', fontWeight: 500, fontSize: '0.7rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span>{item.Name}</span>
                                {isAlreadyAdded && (
                                  <Chip label="Added" size="small" color="success" sx={{ fontSize: '0.55rem', height: 12 }} />
                                )}
                              </Box>
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.65rem', color: '#6b7280' }}>{item.HSNCode || '-'}</td>
                            <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.65rem', fontWeight: 600, color: '#1e40af' }}>{item.GSTRate || 0}%</td>
                            <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, fontSize: '0.65rem', color: item.BalanceStock > 0 ? '#059669' : '#dc2626' }}>
                              {item.BalanceStock || 0}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{ min: 1 }}
                                  sx={{
                                    width: 45,
                                    '& .MuiInputBase-input': {
                                      py: 0.15,
                                      px: 0.15,
                                      fontSize: '0.65rem',
                                      textAlign: 'center'
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedRate}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  sx={{
                                    width: 65,
                                    '& .MuiInputBase-input': {
                                      py: 0.15,
                                      px: 0.15,
                                      fontSize: '0.65rem',
                                      textAlign: 'right'
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                                  ₹{(item.SaleRate || 0).toFixed(2)}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountPercentage}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                  inputProps={{ min: 0, max: 100 }}
                                  sx={{
                                    width: 40,
                                    '& .MuiInputBase-input': {
                                      py: 0.15,
                                      px: 0.15,
                                      fontSize: '0.65rem',
                                      textAlign: 'center'
                                    }
                                  }}
                                />
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#1e40af', fontSize: '0.7rem' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                  const amount = qty * rate;
                                  const discAmt = (amount * discPct) / 100;
                                  const taxable = amount - discAmt;
                                  const gstAmt = formData.IsGST ? (taxable * (item.GSTRate || 0)) / 100 : 0; // Set GST to 0 if IsGST is false
                                  const total = taxable + gstAmt;
                                  return `₹${(Math.round(total * 100) / 100).toFixed(2)}`;
                                })()
                              ) : (
                                <span style={{ color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {getPaginatedItems().length === 0 && (
                        <tr>
                          <td colSpan="9" style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                            {getItemsSearchValue ? 'No items found matching your search' : 'No items available'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Pagination */}
              {getTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setCurrentItemsPage(prev => Math.max(1, prev - 1))}
                      disabled={currentItemsPage === 1}
                      sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
                    >
                      Previous
                    </Button>
                    <Typography variant="caption" sx={{ mx: 1.5 }}>
                      Page {currentItemsPage} of {getTotalPages}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setCurrentItemsPage(prev => Math.min(getTotalPages, prev + 1))}
                      disabled={currentItemsPage === getTotalPages}
                      sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            {selectedItems.length > 0 && `${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''} selected`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleCloseGetItemsModal}
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.75rem', py: 0.25, px: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedItems}
              variant="contained"
              size="small"
              disabled={selectedItems.length === 0 || addingItemsLoading}
              sx={{
                bgcolor: '#1e40af',
                '&:hover': { bgcolor: '#1d4ed8' },
                fontSize: '0.75rem',
                py: 0.25,
                px: 1.5
              }}
            >
              {addingItemsLoading ? (
                <>
                  <CircularProgress size={12} sx={{ mr: 0.5 }} />
                  Adding...
                </>
              ) : (
                `Add Selected (${selectedItems.length})`
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaleModal;